import { useCallback } from 'react';

import { type ListingApplicationPayload } from '#/api/ats';
import type { ApiRequestedAttachment } from '#/api/types/ApiRequestedAttachment';
import type { ApiOrgId, ApiVolopId } from '#/api/types/ApiTypedId';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { embedSearchWidgetApiCreateListingApplication } from '#/modules/embedSearchWidget/api/listingApplication/embedSearchWidgetApiCreateListingApplication';
import type { EmbedSearchWidgetApiMetadata } from '#/modules/embedSearchWidget/api/types/EmbedSearchWidgetApiMetadata';
import { EmbedSearchWidgetRequestedAttachmentUserField } from '#/modules/embedSearchWidget/fields/EmbedSearchWidgetRequestedAttachmentUserField';
import { trackAppliedToListing } from '#/modules/listing/analytics/trackAppliedToListing';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import { ListingViewApplyAts } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAts';
import { ListingViewAtsApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewAtsApplicationForm';
import { trackViewListingAtsSection } from '#/modules/listing/helpers/trackViewListingAtsSection';
import type { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';

type Props = {
  embedSearchWidget: EmbedSearchWidgetApiMetadata;
  volopId: ApiVolopId;
  applied: boolean;
  applyText: string | null;
  requestedAttachments: ApiRequestedAttachment[];
  requestedAttachmentsHash: string;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  onApplied: () => void;
};

export function EmbedSearchWidgetViewVolopPageAtsApply({
  embedSearchWidget,
  volopId,
  applied,
  applyText,
  requestedAttachments,
  requestedAttachmentsHash,
  orgId,
  orgType,
  onApplied,
}: Props) {
  const { i18n } = useI18n();

  const combinedRequestedAttachments = [
    ...requestedAttachments,
    ...embedSearchWidget.requestedAttachments,
  ];

  const onApply: FormSubmitFunction<ListingApplicationPayload> = useCallback(
    async ({ values, handleError }) => {
      try {
        await embedSearchWidgetApiCreateListingApplication({
          listingId: volopId,
          listingType: 'VOLOP',
          searchWidgetId: embedSearchWidget.id,
          email: values.email ?? '',
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          resumeS3FileId: null,
          requestedAttachments: values.requestedAttachments,
          requestedAttachmentsHash,
        });

        trackAppliedToListing({
          listingId: volopId,
          listingType: 'VOLOP',
          orgId: orgId ?? null,
          orgType: orgType ?? null,
          isNewResume: true,
          hasAts: true,
        });

        onApplied();
      } catch (error) {
        handleError(error as Error);
      }
    },
    [
      embedSearchWidget.id,
      requestedAttachmentsHash,
      orgId,
      orgType,
      volopId,
      onApplied,
    ],
  );

  if (applied) return <ListingViewAppliedMessage />;

  return (
    <ListingViewApplyAts
      applyText={applyText}
      onReach={() =>
        trackViewListingAtsSection({
          listingId: volopId,
          listingType: 'VOLOP',
          orgId,
          orgType,
          applied: false,
        })
      }
      applicationForm={
        <ListingViewAtsApplicationForm
          listingId={volopId}
          listingType="VOLOP"
          requestedAttachments={combinedRequestedAttachments}
          submission={{
            listingApplicationInitialValues: {},
            listingApplicationInitialErrors: {},
            onSubmit: onApply,
            requestedAttachmentsHash,
          }}
          submitButtonText={
            embedSearchWidget.buttonText ?? i18n('Submit Your Form')
          }
          renderRequestedAttachmentFileField={({
            key,
            name,
            label,
            disabled,
            requestedFileAttachment,
          }) => (
            <EmbedSearchWidgetRequestedAttachmentUserField
              key={key}
              name={name}
              label={label}
              disabled={disabled}
              analyticsLabel={requestedFileAttachment.label}
              downloadAttachment={false}
              fileType="LISTING_APPLICATION"
              listingType="VOLOP"
            />
          )}
          renderResumeField={
            // Volop doesn't have a resume field
            () => null
          }
        />
      }
    />
  );
}
